import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import usePageMeta from "../../lib/usePageMeta";

const API = process.env.REACT_APP_BACKEND_URL;
const LS_TOKEN = "ccchaps.fd2gfac.token";

// ---------------------------------------------------------------------------
// FD2G-FAC-001 · Enrollment / PayPal Sandbox checkout
// ---------------------------------------------------------------------------
// Steps: email → (register | signin) → review → pay(PayPal SDK) → success|error
// Server-side capture is the authoritative access gate. Reaching this page,
// or a success URL, never grants access on its own.
// ---------------------------------------------------------------------------

export default function AcademyFd2gFacEnroll() {
  usePageMeta({
    title: "Enroll · FD2G-FAC-001 · The Five Voices of the Journey",
    description: "Certified Facilitator Training enrollment (PayPal Sandbox).",
    noindex: true,
  });

  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [existing, setExisting] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", password: "" });
  const [token, setToken] = useState(() => localStorage.getItem(LS_TOKEN) || "");
  const [pricing, setPricing] = useState(null);
  const [ppConfig, setPpConfig] = useState(null);
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const authed = useMemo(
    () => axios.create({
      baseURL: `${API}/api`,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }),
    [token],
  );

  useEffect(() => {
    // Bootstrap: fetch pricing + PayPal config in parallel.
    axios.get(`${API}/api/portal/fd2g-fac-001/pricing`).then((r) => setPricing(r.data)).catch(() => {});
    axios.get(`${API}/api/portal/fd2g-fac-001/paypal-config`).then((r) => setPpConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (params.get("step") === "canceled") {
      setResult({ status: "canceled", ux_state: "canceled" });
      setStep("error");
    }
  }, [params]);

  useEffect(() => {
    // If a learner token exists, skip email step. If the learner is already
    // entitled, jump straight to success.
    if (token && step === "email") {
      (async () => {
        try {
          const r = await authed.get("/portal/fd2g-fac-001/entitlement");
          if (r.data?.entitled) {
            setResult({ status: "completed", entitlement_active: true, ux_state: "activated", already: true });
            setStep("success");
          } else {
            setStep("review");
          }
        } catch {
          setStep("review");
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const priceUsd = pricing?.price_usd || "149.00";
  const priceLabel = `$${Number(priceUsd).toFixed(0)} USD`;

  // --------------------------- Auth phase --------------------------------
  const submitEmail = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/api/portal/fd2g-fac-001/check-email`, { email });
      setExisting(!!r.data.exists);
      setStep(r.data.exists ? "signin" : "register");
    } catch (e) { setErr(e?.response?.data?.detail || "Unable to check email."); }
    finally { setBusy(false); }
  };

  const submitRegister = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/api/portal/fd2g-fac-001/register`, {
        email, password: form.password,
        first_name: form.first_name, last_name: form.last_name,
        course_comms_consent: true, privacy_policy_agreed: true,
        terms_agreed: true, code_of_conduct_agreed: true,
      });
      setToken(r.data.access_token);
      localStorage.setItem(LS_TOKEN, r.data.access_token);
      setStep("review");
    } catch (e) { setErr(e?.response?.data?.detail || "Registration failed."); }
    finally { setBusy(false); }
  };

  const submitSignin = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/api/portal/auth/login`, { email, password: form.password });
      setToken(r.data.access_token);
      localStorage.setItem(LS_TOKEN, r.data.access_token);
      setStep("review");
    } catch (e) { setErr(e?.response?.data?.detail || "Sign-in failed."); }
    finally { setBusy(false); }
  };

  // --------------------------- Order phase -------------------------------
  const startOrder = async () => {
    setBusy(true); setErr("");
    try {
      const r = await authed.post("/portal/fd2g-fac-001/orders", { course_code: "FD2G-FAC-001" });
      setOrder(r.data);
      setStep("pay");
    } catch (e) {
      if (e?.response?.status === 409) {
        setResult({ status: "completed", entitlement_active: true, ux_state: "activated", already: true });
        setStep("success");
      } else {
        setErr(e?.response?.data?.detail || "Could not create order.");
      }
    }
    finally { setBusy(false); }
  };

  // Called by PayPalButtons.createOrder — must return the PayPal order id.
  const paypalCreateOrder = async () => {
    // Create the order on our server first so we hold the authoritative record
    // before PayPal ever sees the buyer. The server calls PayPal to mint the
    // real order id and returns it.
    const r = await authed.post("/portal/fd2g-fac-001/orders", { course_code: "FD2G-FAC-001" });
    setOrder(r.data);
    return r.data.paypal_order_id;
  };

  // Called by PayPalButtons.onApprove — capture through our server (never trust
  // the frontend).
  const paypalOnApprove = async (data) => {
    setBusy(true); setErr("");
    try {
      const r = await authed.post(`/portal/fd2g-fac-001/orders/${data.orderID}/capture`, {});
      setResult(r.data);
      setStep(r.data.status === "completed" && r.data.entitlement_active ? "success" : "error");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Capture failed.");
      setStep("error");
    } finally { setBusy(false); }
  };

  const paypalOnCancel = () => { setResult({ status: "canceled", ux_state: "canceled" }); setStep("error"); };
  const paypalOnError = (e) => { setErr((e && e.message) || "PayPal error"); setStep("error"); };

  // ------------------------------ UI -------------------------------------
  return (
    <div className="min-h-screen bg-[#FDFCF6]" data-testid="fd2gfac-enroll-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-2">
          CCCHAPS Academy · FD2G-FAC-001
        </p>
        <h1
          className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight"
          data-testid="fd2gfac-enroll-title"
        >
          Enroll — The Five Voices of the Journey
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Certified Facilitator Training · {pricing?.description || "Founding Facilitator"} · {priceLabel}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-slate-50 border border-slate-200 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-700">
            Individual Certification
          </span>
          <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-900">
            PayPal Sandbox
          </span>
        </div>

        <ol className="mt-8 flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">
          {["email", "account", "review", "pay", "done"].map((label, i) => (
            <li
              key={label}
              className={`px-2 py-1 rounded-full border ${indexActive(step, i) ? "border-[#4A6FA5] bg-[#F0F4F8] text-[#4A6FA5]" : "border-slate-200 bg-white"}`}
            >
              {i + 1}. {label}
            </li>
          ))}
        </ol>

        {err && (
          <div className="mt-5 rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-2 text-sm" data-testid="fd2gfac-enroll-error">
            {err}
          </div>
        )}

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
          {step === "email" && (
            <form onSubmit={submitEmail} className="space-y-4" data-testid="fd2gfac-enroll-step-email">
              <p className="text-sm text-slate-600">
                Enter your email to begin. If you already have an Academy
                account, we&rsquo;ll ask you to sign in — we won&rsquo;t create
                a duplicate.
              </p>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600">Email</span>
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="fd2gfac-enroll-email"
                  className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-sm"
                />
              </label>
              <button
                type="submit" disabled={busy} data-testid="fd2gfac-enroll-email-continue"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white font-medium disabled:opacity-60"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}

          {step === "signin" && (
            <form onSubmit={submitSignin} className="space-y-4" data-testid="fd2gfac-enroll-step-signin">
              <p className="text-sm text-slate-700">
                <strong>This email is already registered.</strong> Sign in to continue with your enrollment.
              </p>
              <p className="text-sm text-slate-500">{email}</p>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600">Password</span>
                <input
                  type="password" required value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="fd2gfac-enroll-password"
                  className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-sm"
                />
              </label>
              <button
                type="submit" disabled={busy} data-testid="fd2gfac-enroll-signin"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white font-medium disabled:opacity-60"
              >
                Sign In <ArrowRight className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => setStep("email")} className="ml-3 text-xs text-slate-500 hover:underline">
                Use a different email
              </button>
            </form>
          )}

          {step === "register" && (
            <form onSubmit={submitRegister} className="space-y-4" data-testid="fd2gfac-enroll-step-register">
              <p className="text-sm text-slate-600">
                Create your Academy account. <strong>Your name will appear on your Certified Facilitator credential and certificate.</strong>
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600">First name</span>
                  <input
                    required value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    data-testid="fd2gfac-enroll-first-name"
                    className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600">Last name</span>
                  <input
                    required value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    data-testid="fd2gfac-enroll-last-name"
                    className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-sm"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600">Email</span>
                <input readOnly value={email} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600">Password (8+ chars)</span>
                <input
                  type="password" required minLength={8} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="fd2gfac-enroll-register-password"
                  className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-sm"
                />
              </label>
              <p className="text-xs text-slate-500">
                By continuing you agree to CCCHAPS Academy&rsquo;s privacy policy, terms, code of conduct, and to receive course-related emails.
              </p>
              <button
                type="submit" disabled={busy} data-testid="fd2gfac-enroll-register"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white font-medium disabled:opacity-60"
              >
                Create Account <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}

          {step === "review" && (
            <div data-testid="fd2gfac-enroll-step-review">
              <p className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600 mb-2">Order review</p>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <p className="font-heading text-xl text-slate-900">The Five Voices of the Journey — Certified Facilitator Training</p>
                <p className="text-sm text-slate-600">Course ID: FD2G-FAC-001 · {pricing?.description || "Founding Facilitator"} · Individual Certification</p>
                <div className="mt-4 flex items-baseline justify-between">
                  <span className="text-sm text-slate-600">Total</span>
                  <span className="font-heading text-3xl text-slate-900" data-testid="fd2gfac-enroll-price">{priceLabel}</span>
                </div>
                <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600 leading-relaxed" data-testid="fd2gfac-enroll-scope">
                  <p className="font-semibold text-slate-800">This purchase is for one individual&rsquo;s certification.</p>
                  <p className="mt-1">
                    Does not include organizational or site licensing, permission to
                    certify other facilitators, sublicensing rights, or organization-wide
                    implementation rights. Organizational Licensing is a separate FD2G pathway.
                  </p>
                </div>
              </div>
              <button
                onClick={startOrder} disabled={busy} data-testid="fd2gfac-enroll-to-paypal"
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white font-medium disabled:opacity-60"
              >
                Continue to PayPal <ArrowRight className="w-4 h-4" />
              </button>
              <p className="mt-3 text-[11px] text-slate-500">
                Sandbox mode · no real money will be charged. Access is granted only after PayPal confirms a completed capture on the server.
              </p>
            </div>
          )}

          {step === "pay" && order && (
            <div data-testid="fd2gfac-enroll-step-pay">
              <p className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600 mb-2">PayPal Sandbox Checkout</p>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700 leading-relaxed">
                <p><strong>Order:</strong> <code className="text-xs">{order.paypal_order_id}</code></p>
                <p className="mt-1"><strong>Amount:</strong> {priceLabel} · {order.environment}</p>
                <p className="mt-2 text-slate-600">Complete payment through PayPal below. Our server verifies the capture directly with PayPal before granting course access.</p>
              </div>
              <div className="mt-6" data-testid="fd2gfac-enroll-paypal-container">
                {ppConfig?.configured ? (
                  <PayPalScriptProvider
                    options={{
                      "client-id": ppConfig.client_id,
                      currency: ppConfig.currency || "USD",
                      intent: "capture",
                    }}
                  >
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                      createOrder={paypalCreateOrder}
                      onApprove={paypalOnApprove}
                      onCancel={paypalOnCancel}
                      onError={paypalOnError}
                      forceReRender={[priceUsd, order.paypal_order_id]}
                    />
                  </PayPalScriptProvider>
                ) : (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="fd2gfac-enroll-paypal-unconfigured">
                    <p className="flex items-center gap-2 font-semibold">
                      <AlertTriangle className="w-4 h-4" /> Enrollment opening soon
                    </p>
                    <p className="mt-2 text-amber-800">
                      Online payment is currently being prepared. Please check back.
                    </p>
                  </div>
                )}
              </div>
              <div className="mt-6">
                <Link to="/academy/fd2g-fac-001" className="text-xs text-slate-500 hover:underline">Cancel and return</Link>
              </div>
              <p className="mt-3 text-[11px] text-slate-500">
                Refreshing this page or navigating away and back will not create duplicate purchases or duplicate access — the server enforces idempotency by PayPal order id.
              </p>
            </div>
          )}

          {step === "success" && (
            <div className="text-center" data-testid="fd2gfac-enroll-step-success">
              <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" strokeWidth={1.5} />
              <p className="mt-4 text-[11px] uppercase tracking-[0.24em] text-emerald-700 font-semibold">Enrollment Complete</p>
              <h2 className="mt-2 font-heading text-3xl text-slate-900">
                You&rsquo;re enrolled in the Certified Facilitator Training.
              </h2>
              <p className="mt-3 text-sm text-slate-600">
                {result?.paypal_capture_id && (<>Capture reference: <code className="text-xs">{result.paypal_capture_id}</code> · </>)}
                Access granted after server verification.
              </p>
              <button
                onClick={() => nav("/academy/portal/fd2g-fac-001/learn")}
                data-testid="fd2gfac-enroll-begin"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium"
              >
                Begin Course <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {step === "error" && (
            <div className="text-center" data-testid="fd2gfac-enroll-step-error">
              <p className="text-[11px] uppercase tracking-[0.24em] text-amber-700 font-semibold">Payment {result?.status || "issue"}</p>
              <h2 className="mt-2 font-heading text-2xl text-slate-900">Access has not been granted.</h2>
              <p className="mt-3 text-sm text-slate-600 max-w-lg mx-auto leading-relaxed">
                {result?.status === "pending" && "PayPal reports the payment is still processing. Course access will unlock automatically once the capture completes."}
                {result?.status === "canceled" && "Checkout was canceled. No charge was made. You can try again anytime."}
                {result?.status === "failed" && "The payment was not completed. You may retry with the same account."}
                {!result?.status && "The transaction did not complete."}
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <button onClick={() => { setErr(""); setStep("review"); }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium">
                  Try again
                </button>
                <Link to="/academy/fd2g-fac-001" className="text-xs text-slate-500 hover:underline">Return to program overview</Link>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5 text-xs text-slate-500 leading-relaxed" data-testid="fd2gfac-enroll-safeguards">
          <p>
            <ShieldCheck className="inline w-3.5 h-3.5 text-[#4A6FA5] mr-1" />
            <strong>Safeguards active:</strong> access is granted only after a server-side capture verification with PayPal. Direct navigation to a success URL, page refreshes, or duplicate PayPal webhook deliveries cannot create duplicate purchases or duplicate access.
          </p>
        </div>
      </div>
    </div>
  );
}

function indexActive(step, i) {
  const stepIndex = { email: 0, register: 1, signin: 1, review: 2, pay: 3, success: 4, error: 4 };
  return (stepIndex[step] ?? 0) >= i;
}
